import type { CartTransformCreateMutationVariables } from '~/api/shopify/mutations/cartTransformCreate.generated';

import { useCartTransformCreateMutation } from '~/api/shopify/mutations/cartTransformCreate.generated';
import { useCartTransformDeleteMutation } from '~/api/shopify/mutations/cartTransformDelete.generated';
import { useCartTransformsQuery } from '~/api/shopify/queries/cartTransforms.generated';
import { SHOPIFY_CART_TRANSFORM_FUNCTION_HANDLE } from '~/constants/shopify';
import { captureSentryError } from '~/exceptions/sentry';
import { getConfig } from '~/config/environment';
import { useCallback, useState } from 'react';

// The Function is addressed two different ways: `cartTransforms` only exposes the
// deploy-scoped `functionId` (so it stays the key we look our own transform up by),
// while `cartTransformCreate` takes the app-scoped `functionHandle` since 2025-10.
const FUNCTION_ID = getConfig('APP')?.SHOPIFY_CART_TRANSFORMER_ID ?? '';
export const useCartTransformUpdate = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // Query to check existing cart transforms
  const { data, refetch, isLoading } = useCartTransformsQuery({ first: 20 });

  const cartTransformDeleteMutation = useCartTransformDeleteMutation();
  const cartTransformCreateMutation = useCartTransformCreateMutation();

  // Helper to find existing transform by functionId
  const getExistingTransform = useCallback(
    () => data?.cartTransforms?.edges.find((edge) => edge.node.functionId === FUNCTION_ID),
    [data],
  );

  // Update handler triggered by action
  const handleUpdateMetafield = useCallback(
    async (metafields: CartTransformCreateMutationVariables['metafields']) => {
      try {
        setIsUpdating(true);
        const existingTransform = getExistingTransform();

        // Step 1: Delete existing transform if it exists
        if (existingTransform) {
          const deleted = await cartTransformDeleteMutation.mutateAsync({
            id: existingTransform.node.id,
          });
          const deleteErrors = deleted.cartTransformDelete?.userErrors ?? [];
          if (deleteErrors.length) {
            throw new Error(
              `cartTransformDelete failed: ${deleteErrors.map((e) => e.message).join('; ')}`,
            );
          }
        }

        // Step 2: Create a new transform
        const created = await cartTransformCreateMutation.mutateAsync({
          functionHandle: SHOPIFY_CART_TRANSFORM_FUNCTION_HANDLE,
          metafields,
        });
        const createErrors = created.cartTransformCreate?.userErrors ?? [];
        if (createErrors.length || !created.cartTransformCreate?.cartTransform) {
          throw new Error(
            `cartTransformCreate failed: ${createErrors.map((e) => e.message).join('; ')}`,
          );
        }

        // Refetch to update the cache
        await refetch();
      } catch (error) {
        setUpdateError(error as Error);
        // The mutations resolve on userErrors too, so without this the shop can end up
        // with no cart transform at all and nothing recorded anywhere.
        captureSentryError(error, {
          source: 'useCartTransformUpdate.handleUpdateMetafield',
          tags: { shopify: 'Shopify' },
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [getExistingTransform, refetch],
  );

  return { handleUpdateMetafield, isUpdating, updateError, isLoading };
};
